import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button, Alert, FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip, Menu, useTheme, useMediaQuery } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GridOnIcon from '@mui/icons-material/GridOn';
import GridOffIcon from '@mui/icons-material/GridOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { DashboardProvider, useDashboard } from '../contexts/DashboardContext';
import { getDashboardConfig, updateDashboardConfig, listDashboardConfigs, recordAppUsage } from '../api/dashboardConfig.api';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import GlobalFiltersBar from '../components/Dashboard/GlobalFiltersBar';
import DashboardChatBar from '../components/Dashboard/DashboardChatBar';
import AppHeader from '../components/Dashboard/AppHeader';
import AppThemeProvider from '../components/Dashboard/AppThemeProvider';

function DashboardRuntimeContent({ config, dashboards, darkMode, onLayoutChange, toggleLayout, savingLayout }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSFF = useMediaQuery(theme.breakpoints.down('md'));
  const [overflowMenuAnchor, setOverflowMenuAnchor] = useState(null);
  const { loading, error, setError, runAllPanelFlows } = useDashboard();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <AppHeader config={config} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, p: { xs: 1, sm: 2 }, borderBottom: 1, borderColor: 'divider', flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate('/dashboard-builder')} sx={{ minWidth: 44, minHeight: 44 }}>
          {isSFF ? null : 'List'}
        </Button>
        <Button
          startIcon={<EditIcon />}
          variant="contained"
          size={isSFF ? 'small' : 'medium'}
          color="secondary"
          onClick={() => navigate(`/dashboard-builder/edit/${config._id || config.id}`)}
          sx={{ minHeight: 44 }}
        >
          {isSFF ? 'Edit' : 'Edit app'}
        </Button>
        {!isSFF && (
          <Button startIcon={<PlayArrowIcon />} size="small" variant="outlined" disabled>
            Play
          </Button>
        )}
        <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 200 }, minHeight: 44 }}>
          <InputLabel>App</InputLabel>
          <Select
            value={config._id || config.id || ''}
            label="App"
            onChange={(e) => navigate(`/dashboard-builder/run/${e.target.value}`)}
          >
            {dashboards.map((d) => (
              <MenuItem key={d._id || d.id} value={d._id || d.id}>
                {d.name || 'Untitled App'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="h6" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {config.name || 'App'}
        </Typography>
        {isSFF ? (
          <>
            {loading && <CircularProgress size={24} />}
            <IconButton size="large" onClick={(e) => setOverflowMenuAnchor(e.currentTarget)} sx={{ minWidth: 44, minHeight: 44 }}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={overflowMenuAnchor}
              open={!!overflowMenuAnchor}
              onClose={() => setOverflowMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem onClick={() => { runAllPanelFlows(); setOverflowMenuAnchor(null); }} disabled={loading}>
                <RefreshIcon fontSize="small" sx={{ mr: 1 }} /> Refresh all
              </MenuItem>
              <MenuItem onClick={() => { toggleLayout(); setOverflowMenuAnchor(null); }} disabled={savingLayout}>
                {config.layout === 'grid' ? <GridOffIcon fontSize="small" sx={{ mr: 1 }} /> : <GridOnIcon fontSize="small" sx={{ mr: 1 }} />}
                {config.layout === 'grid' ? 'Fixed layout' : 'Grid layout'}
              </MenuItem>
            </Menu>
          </>
        ) : (
          <>
            <Tooltip title="Refresh all panels">
              <span>
                <IconButton size="small" onClick={runAllPanelFlows} disabled={loading} sx={{ minWidth: 44, minHeight: 44 }}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={config.layout === 'grid' ? 'Switch to fixed layout' : 'Switch to grid layout'}>
              <IconButton size="small" onClick={toggleLayout} disabled={savingLayout} sx={{ minWidth: 44, minHeight: 44 }}>
                {config.layout === 'grid' ? <GridOffIcon /> : <GridOnIcon />}
              </IconButton>
            </Tooltip>
            {loading && <CircularProgress size={20} />}
          </>
        )}
        {error && (
          <Alert severity="error" sx={{ py: 0, width: '100%' }} onClose={() => setError(null)}>
            {typeof error === 'string' ? error : error?.message || 'Error'}
          </Alert>
        )}
      </Box>
      {!isSFF && (
        <Box sx={{ px: 2, py: 0.5, bgcolor: 'action.hover', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            To configure panels and settings, click <strong>Edit app</strong> above.
          </Typography>
        </Box>
      )}
      {config?.chatPosition === 'header' && (
        <DashboardChatBar darkMode={darkMode} />
      )}
      <GlobalFiltersBar />
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <DashboardLayout config={config} darkMode={darkMode} onLayoutChange={onLayoutChange} />
      </Box>
    </Box>
  );
}

function DashboardRuntimeInner({ darkMode }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [dashboards, setDashboards] = useState([]);
  const [savingLayout, setSavingLayout] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [dashRes] = await Promise.all([
        listDashboardConfigs(),
      ]);
      if (!mounted) return;
      setDashboards(dashRes.data ?? []);
      if (!id) {
        if (dashRes.data?.length) {
          navigate(`/dashboard-builder/run/${dashRes.data[0]._id || dashRes.data[0].id}`, { replace: true });
        } else {
          setLoadError('No apps found');
        }
        return;
      }
      setLoadError(null);
      const { data, error: err } = await getDashboardConfig(id);
      if (!mounted) return;
      if (err) {
        setLoadError(err);
        setConfig(null);
        return;
      }
      setConfig(data);
    })();
    return () => { mounted = false; };
  }, [id, navigate]);

  const handleLayoutChange = async (newLayout) => {
    if (!config?._id || !config?.id) return;
    setSavingLayout(true);
    const { error: err } = await updateDashboardConfig(config._id || config.id, {
      gridLayout: newLayout,
      layout: 'grid',
    });
    if (!err) {
      setConfig((prev) => ({ ...prev, gridLayout: newLayout, layout: 'grid' }));
    }
    setSavingLayout(false);
  };

  const toggleLayout = async () => {
    if (!config?._id || !config?.id) return;
    const newLayout = config.layout === 'grid' ? 'dynamic' : 'grid';
    setSavingLayout(true);
    const { error: err } = await updateDashboardConfig(config._id || config.id, {
      layout: newLayout,
    });
    if (!err) {
      setConfig((prev) => ({ ...prev, layout: newLayout }));
    }
    setSavingLayout(false);
  };

  if (loadError) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/dashboard-builder')}>
          Back to App Builder
        </Button>
      </Box>
    );
  }

  if (!config) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  const recordUsage = useCallback((event) => {
    if (config?._id || config?.id) {
      recordAppUsage(config._id || config.id, event);
    }
  }, [config]);

  useEffect(() => {
    if (config?._id || config?.id) {
      recordAppUsage(config._id || config.id, 'view');
    }
  }, [config?._id, config?.id]);

  return (
    <AppThemeProvider config={config} darkMode={darkMode}>
      <DashboardProvider config={{ ...config, recordUsage }}>
        <DashboardRuntimeContent
        config={config}
        dashboards={dashboards}
        darkMode={darkMode}
        onLayoutChange={handleLayoutChange}
        toggleLayout={toggleLayout}
        savingLayout={savingLayout}
      />
      </DashboardProvider>
    </AppThemeProvider>
  );
}

export default function DashboardRuntimePage({ darkMode }) {
  return <DashboardRuntimeInner darkMode={darkMode} />;
}
